import { supabase } from "./supabase";
import { saveGoalsRemote, type Goals } from "./goals";
import { saveWeight, type WeightUnit } from "./weight";

export type Sex = "male" | "female";
export type ActivityLevel = "sedentary" | "light" | "moderate" | "very" | "extreme";
export type Goal = "lose" | "maintain" | "gain";

const ACTIVITY_MULTIPLIERS: Record<ActivityLevel, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  very: 1.725,
  extreme: 1.9
};

const GOAL_ADJUSTMENT: Record<Goal, number> = {
  lose: -500, // ~1 lb/week deficit
  maintain: 0,
  gain: 300 // ~0.6 lb/week surplus
};

const LBS_PER_KG = 2.2046226218;
const IN_PER_CM = 0.3937007874;

export function lbsToKg(lbs: number): number {
  return lbs / LBS_PER_KG;
}
export function kgToLbs(kg: number): number {
  return kg * LBS_PER_KG;
}
export function inToCm(inches: number): number {
  return inches / IN_PER_CM;
}
export function cmToIn(cm: number): number {
  return cm * IN_PER_CM;
}

/** Mifflin-St Jeor: most accurate widely-accepted BMR estimator. */
export function computeBMR(sex: Sex, weight_kg: number, height_cm: number, age: number): number {
  const base = 10 * weight_kg + 6.25 * height_cm - 5 * age;
  return sex === "male" ? base + 5 : base - 161;
}

export function computeTDEE(bmr: number, activity: ActivityLevel): number {
  return bmr * ACTIVITY_MULTIPLIERS[activity];
}

export function recommendedCalories(tdee: number, goal: Goal): number {
  return Math.round(tdee + GOAL_ADJUSTMENT[goal]);
}

/** Default 30/40/30 split converted to grams (P*4 + C*4 + F*9 = calories). */
export function recommendedMacros(calories: number): Pick<Goals, "protein_g" | "carbs_g" | "fat_g"> {
  return {
    protein_g: Math.round((calories * 0.3) / 4),
    carbs_g: Math.round((calories * 0.4) / 4),
    fat_g: Math.round((calories * 0.3) / 9)
  };
}

// ---------------------------------------------------------------------------
// Status + completion
// ---------------------------------------------------------------------------

export interface OnboardingStatus {
  onboarded: boolean;
}

export async function loadOnboardingStatus(): Promise<OnboardingStatus> {
  if (!supabase) return { onboarded: true };
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) return { onboarded: true };
  const userId = userData.user.id;

  // Primary signal: explicit onboarded_at marker on user_prefs.
  // If the column doesn't exist yet (migration not run), this throws — fall
  // through to the heuristic below rather than incorrectly skipping the wizard.
  try {
    const { data: prefsRow } = await supabase
      .from("user_prefs")
      .select("onboarded_at")
      .eq("user_id", userId)
      .maybeSingle();
    if (prefsRow?.onboarded_at) return { onboarded: true };
  } catch {
    // ignore — column or row may not exist
  }

  // Fallback heuristic: a user who has logged any meals is effectively
  // onboarded (treats legacy users gracefully without forcing them through).
  try {
    const { count } = await supabase
      .from("meals")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId);
    if ((count ?? 0) > 0) return { onboarded: true };
  } catch {
    // ignore
  }

  return { onboarded: false };
}

export interface OnboardingResult {
  sex: Sex;
  age: number;
  weight_kg: number;
  height_cm: number;
  activity: ActivityLevel;
  goal: Goal;
  unit: WeightUnit;
  goals: Goals;
}

export async function completeOnboarding(result: OnboardingResult): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  const { data: userData } = await supabase.auth.getUser();
  const user = userData.user;
  if (!user) throw new Error("Not signed in");

  // Save weight unit preference + onboarded_at in one upsert.
  const { error: prefsErr } = await supabase.from("user_prefs").upsert({
    user_id: user.id,
    weight_unit: result.unit,
    onboarded_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
  if (prefsErr) throw prefsErr;

  // Persist as separate side effects — if any fail the rest still went through.
  await saveGoalsRemote(result.goals);
  await saveWeight(result.weight_kg);
}

/** Marks onboarding complete using default goals — used by the Skip path. */
export async function skipOnboarding(): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  const { data: userData } = await supabase.auth.getUser();
  const user = userData.user;
  if (!user) throw new Error("Not signed in");
  const { error } = await supabase.from("user_prefs").upsert({
    user_id: user.id,
    onboarded_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
  if (error) throw error;
}

export function activityLabel(a: ActivityLevel): string {
  return {
    sedentary: "Sedentary — desk job, little exercise",
    light: "Lightly active — light exercise 1-3 days/week",
    moderate: "Moderately active — moderate exercise 3-5 days/week",
    very: "Very active — hard exercise 6-7 days/week",
    extreme: "Extremely active — physical job + daily training"
  }[a];
}

export function goalLabel(g: Goal): string {
  return {
    lose: "Lose weight (~1 lb/week)",
    maintain: "Maintain my weight",
    gain: "Gain weight (~0.6 lb/week)"
  }[g];
}
